import i18n from 'i18next';
import { Duration, Money } from '../types';
import { Airline } from '../schemas/Airline';
import * as React from 'react';
import { Leg, MinPrice, SimpleFlight } from './types';
import memes from '../memes';
import { SelectedFare } from '../DesktopFlight/types';
import { getDateWithoutTimezone } from '../Engine/components/FastSearch/utils';
import {
	AviaPassengerType,
	AviaSearchParameters,
	FareFamilyFragment,
	FareFamilyOptionFragment,
	FareFamilyPriceFragment,
	FareFeaturePaymentType,
	FlightClass,
	FlightDirectionFragment,
	FlightSegmentStatus,
	PassengerTypeSearchParameter,
	SearchParametersFragment,
	SearchResultsQuery,
	SegmentWithTransferFragment
} from '@websky/graphql';
import { getCheapestFlightInCombination } from '../DesktopFlight/utils';

/**
 * Sum up multiple duration objects.
 */
export const sumDuration = (...durations: Duration[]): Duration => {
	const result: Duration = {
		days: 0,
		hours: 0,
		minutes: 0,
		seconds: 0
	};

	durations.forEach(duration => {
		if (duration) {
			result.days += duration.days ? parseInt(duration.days.toString()) : 0;
			result.hours += duration.hours ? parseInt(duration.hours.toString()) : 0;
			result.minutes += duration.minutes ? parseInt(duration.minutes.toString()) : 0;
			result.seconds += duration.seconds ? parseInt(duration.seconds.toString()) : 0;
		}
	});

	// Fix seconds
	if (result.seconds >= 60) {
		result.minutes += Math.floor(result.seconds / 60);
		result.seconds = result.seconds % 60;
	}

	// Fix minutes
	if (result.minutes >= 60) {
		result.hours += Math.floor(result.minutes / 60);
		result.minutes = result.minutes % 60;
	}

	// Fix hours
	if (result.hours >= 24) {
		result.days += Math.floor(result.hours / 24);
		result.hours = result.hours % 24;
	}

	return result;
};

/**
 * Get info string for given duration object.
 *
 * ex: '1d 12h 20m'
 */
export const getDurationString = (duration: Duration) => {
	const result: string[] = [];

	if (duration.days) {
		result.push(duration.days.toString() + i18n.t('d'));
	}

	if (duration.hours) {
		result.push(duration.hours.toString() + i18n.t('h'));
	}

	if (duration.minutes) {
		result.push(duration.minutes.toString() + i18n.t('m'));
	}

	if (duration.seconds) {
		result.push(duration.seconds.toString() + i18n.t('s'));
	}

	return result.join(' ');
};

/**
 * Convert duration object into the number of seconds.
 */
export const getDurationInSeconds = (inputDuration: Duration): number => {
	// Add missing duration elements (seconds, minutes, etc).
	const duration = sumDuration(inputDuration);

	return duration.seconds + duration.minutes * 60 + duration.hours * 60 * 60 + duration.days * 24 * 60 * 60;
};

/**
 * Get total flight duration.
 */
export const getFlightDuration = (
	segments: SegmentWithTransferFragment[],
	withoutTransfers: boolean = false
): Duration => {
	let duration: Duration = {};

	segments
		.filter(({ segment }) => segment.status !== FlightSegmentStatus.Canceled)
		.forEach(({ segment, transferDuration }) => {
			if (!withoutTransfers) {
				duration = sumDuration(duration, transferDuration);
			}
			duration = sumDuration(duration, segment.duration);
		});

	return duration;
};

/**
 * Get total flight transfers duration.
 */
export const getFlightTransfersDuration = (flight: SimpleFlight): Duration => {
	let duration: Duration = {};

	flight.segments.forEach(segment => {
		duration = sumDuration(duration, segment.transferDuration);
	});

	return duration;
};

/**
 * Break up money string into pieces grouped by thousands.
 *
 * '3500' -> ['3', '500']
 */
export const splitNumber = (money: string): string[] => {
	let result: string[] = [];

	if (money.length < 4) {
		result.push(money);
	} else {
		const firstPart = money.slice(0, -3);

		if (firstPart.length > 3) {
			result = splitNumber(firstPart);
		} else {
			result.push(firstPart);
		}

		result.push(money.slice(-3));
	}

	return result;
};

export const splitMoney = (money: number, fractionDigits?: number, min: number = 10000): string[] => {
	const stringMoney = ((money ^ 0) === money ? money : money.toFixed(fractionDigits || 2)).toString(10);

	// Do not process values under 10k.
	if (money < min) {
		return [stringMoney];
	}

	const parts = stringMoney.split('.');
	let result: string[];

	if (parts.length) {
		if (parts.length === 1) {
			result = splitNumber(stringMoney);
		} else {
			const integerParts = splitNumber(parts[0]);
			const lastElement = integerParts.pop();

			// In case of decimal values like: '1123.18'.
			result = [...integerParts, `${lastElement}.${parts[1]}`];
		}
	}

	return result;
};

/**
 * Extract list of min prices on given groups.
 */
export const getMinPrices = (
	prices: FareFamilyPriceFragment[],
	singleFare?: boolean,
	selectedFares: SelectedFare[] = [],
	findOriginalPrice?: boolean,
	preselectedFlightClass?: FlightClass
): MinPrice[] => {
	let result: MinPrice[];
	let filteredPrices = prices?.length ? [...prices] : [];

	let minEconomyPrice: Money = null;
	let economyFareFamily: FareFamilyFragment = null;
	let minBusinessPrice: Money = null;
	let businessFareFamily: FareFamilyFragment = null;
	let economyPriceInMiles: number = null;
	let businessPriceInMiles: number = null;
	let hasSubsidyFare = false;

	const lastSelectedFare = selectedFares?.[selectedFares.length - 1] ?? null;
	const lastSelectedFareFlightIds = lastSelectedFare?.prices?.map(price => price.flight.id) ?? [];
	const lastSelectedFlightId = lastSelectedFare?.flightInfo?.id ?? null;

	if (preselectedFlightClass) {
		filteredPrices = filteredPrices.filter(price => price.fareFamily.category === preselectedFlightClass);
	}

	if (lastSelectedFare) {
		filteredPrices = filteredPrices.filter(price =>
			price.prices?.some(p => lastSelectedFareFlightIds.includes(p.flight.id))
		);
	}

	if (lastSelectedFlightId && !filteredPrices.length) {
		filteredPrices = filteredPrices.filter(price => price.prices?.some(p => p.flight.id === lastSelectedFlightId));
	}

	if (filteredPrices.length) {
		filteredPrices?.forEach(price => {
			if (price.fareFamily && price.prices?.length) {
				const cheapestFlight = getCheapestFlightInCombination(price, selectedFares);
				const actualPrice = findOriginalPrice ? cheapestFlight.originalPrice : cheapestFlight.price;

				if (price.fareFamily && price.fareFamily.category === FlightClass.Business && !singleFare) {
					if (!minBusinessPrice || minBusinessPrice.amount > actualPrice?.amount) {
						minBusinessPrice = actualPrice;
						businessFareFamily = price.fareFamily;

						if (cheapestFlight?.priceInMiles) {
							businessPriceInMiles = cheapestFlight.priceInMiles;
						}
					}
				} else if (!minEconomyPrice || minEconomyPrice.amount > actualPrice?.amount) {
					minEconomyPrice = actualPrice;
					economyFareFamily = price.fareFamily;

					if (cheapestFlight?.priceInMiles) {
						economyPriceInMiles = cheapestFlight.priceInMiles;
					}

					if (!findOriginalPrice && !hasSubsidyFare && price.prices.some(price => price.flight?.subsidized)) {
						hasSubsidyFare = true;
					}
				}
			}
		});
	}

	result = [
		{
			type: FlightClass.Economy,
			price: minEconomyPrice,
			priceInMiles: economyPriceInMiles,
			fareFamily: economyFareFamily,
			hasSubsidyFare
		}
	];

	if (!singleFare) {
		result = [
			...result,
			{
				type: FlightClass.Business,
				price: minBusinessPrice,
				priceInMiles: businessPriceInMiles,
				fareFamily: businessFareFamily,
				hasSubsidyFare
			}
		];
	}

	return result;
};

/**
 * Extract unique list of airlines on given segments.
 */
export const getAirlinesList = (segments: SegmentWithTransferFragment | SegmentWithTransferFragment[]): Airline[] => {
	const set = new Set<string>();
	const result: Airline[] = [];

	(segments instanceof Array ? segments : [segments]).forEach(({ segment }) => {
		if (!set.has(segment.marketingAirline.iata)) {
			result.push(segment.marketingAirline);
			set.add(segment.marketingAirline.iata);
		}

		if (!set.has(segment.operatingAirline.iata)) {
			result.push(segment.operatingAirline);
			set.add(segment.operatingAirline.iata);
		}
	});

	return result;
};

/**
 * 1. Remove fare groups with the wrong service class
 * 2. Remove fare groups without any prices
 * 3. Filter each fare group prices according to previously selected fares (array intersection by flight ID).
 *
 * #fixme: test me please
 */
export const filterFareGroups = memes(
	(
		fareGroups: FareFamilyPriceFragment[] = [],
		selectedFares: SelectedFare[] = [],
		serviceClass: FlightClass,
		filterPrices?: boolean
	): FareFamilyPriceFragment[] => {
		if (!fareGroups.length) {
			return [];
		}

		return fareGroups
			.filter(
				group =>
					group.fareFamily && group.fareFamily.category === serviceClass && (!filterPrices || group.prices)
			)
			.map(group => {
				if (group.prices && selectedFares.length) {
					const lastSelectedFare = selectedFares[selectedFares.length - 1];
					const selectedMap = {};

					lastSelectedFare.prices.forEach(price => (selectedMap[price.flight.id] = price.flight.id));

					return {
						...group,
						prices: group.prices.filter(price => selectedMap.hasOwnProperty(price.flight.id))
					};
				} else {
					return group;
				}
			});
	}
);

/**
 * Get info string about transfers on given segments.
 *
 * ex: '1h 15m MOW'
 */
export const getTransfersInfo = (flight: SimpleFlight): string => {
	let result = '';

	if (flight.segments.length === 2) {
		result = `${getDurationString(getFlightTransfersDuration(flight))} ${
			flight.segments[0].segment.arrival.airport.iata
		}`;
	} else {
		result = flight.segments
			.slice(0, flight.segments.length - 1)
			.map(({ segment }) => segment.arrival.airport.iata)
			.join(', ');
	}

	return result;
};

export const getUniqueValidOptions = (
	options: FareFamilyOptionFragment[],
	freeOnly = false,
	uniqueOnly = false,
	showUnavailable = false
): FareFamilyOptionFragment[] => {
	const map: {
		[type: string]: boolean;
	} = {};

	return options.filter(option => {
		if (freeOnly && option.availability !== FareFeaturePaymentType.Free) {
			return false;
		}

		if (option.availability === FareFeaturePaymentType.NotAvailable && !showUnavailable) {
			return false;
		} else if (uniqueOnly && map.hasOwnProperty(option.type)) {
			return false;
		} else {
			map[option.type] = true;

			return true;
		}
	});
};

// #fixme: rename
export const convertSearchParameters = (
	searchParameters: SearchParametersFragment,
	promoCode?: string
): AviaSearchParameters => {
	return {
		promotionCode: promoCode,
		currency: searchParameters.currency,
		passengers: searchParameters.passengers.map(passenger => ({
			count: passenger.count,
			passengerType: passenger.passengerType,
			extendedPassengerType: passenger.extendedPassengerType
		})),
		segments: searchParameters.segments.map(segment => ({
			date: segment.date,
			departure: { iata: segment.departure.iata },
			arrival: { iata: segment.arrival.iata }
		})),
		ffpMode: searchParameters.ffpMode
	};
};

export const createLegs = ({ segments }: SearchParametersFragment): Leg[] => {
	return segments.map<Leg>((segment, index) => ({
		id: index,
		departure: segment.departure,
		arrival: segment.arrival,
		date: getDateWithoutTimezone(new Date(segment.date)),
		avaliableDateRange:
			segments.length > 1
				? [
						segments?.[index - 1]?.date ? new Date(segments?.[index - 1]?.date) : null,
						segments?.[index + 1]?.date ? new Date(segments?.[index + 1]?.date) : null
				  ]
				: null,
		isReturnLeg:
			segments.length === 2 &&
			index === 1 &&
			segments[0].arrival.iata === segment.departure.iata &&
			segments[0].departure.iata === segment.arrival.iata
	}));
};

export const createLegsFromFlightDirections = (directions: FlightDirectionFragment[]): Leg[] => {
	return directions.map((direction, key) => {
		const { departure } = direction.legs[0].segments[0].segment,
			{ arrival } = direction.legs[0].segments[direction.legs[0].segments.length - 1].segment;

		return {
			id: key,
			departure: departure.airport,
			arrival: arrival.airport,
			date: getDateWithoutTimezone(new Date(direction.legs[0].segments[0].segment.departure.date)),
			avaliableDateRange:
				directions.length > 1
					? [
							directions?.[key - 1]?.legs[0].segments[0].segment.departure.date
								? new Date(directions?.[key - 1]?.legs[0].segments[0].segment.departure.date)
								: null,
							directions?.[key + 1]?.legs[0].segments[0].segment.departure.date
								? new Date(directions?.[key + 1]?.legs[0].segments[0].segment.departure.date)
								: null
					  ]
					: null,
			isReturnLeg:
				directions.length === 2 &&
				key === 1 &&
				directions[0].legs[0].segments[directions[0].legs[0].segments.length - 1].segment.arrival.airport
					.iata === departure.airport.iata &&
				directions[0].legs[0].segments[0].segment.departure.airport.iata === arrival.airport.iata
		};
	});
};

export const deriveLegs = (searchResults: SearchResultsQuery['SearchResult']): Leg[] => {
	const { searchParameters, flightDirections } = searchResults;

	if (!searchParameters) {
		return [];
	}

	const shouldUseFlightDirections =
		flightDirections.length > 0 && searchParameters.segments.length !== flightDirections.length;

	return shouldUseFlightDirections ? createLegsFromFlightDirections(flightDirections) : createLegs(searchParameters);
};

export const SearchPassengersContext = React.createContext<PassengerTypeSearchParameter[]>([
	{
		count: 1,
		passengerType: AviaPassengerType.ADT,
		extendedPassengerType: null
	}
]);
